import {
    AnyAction,
    createAsyncThunk,
    createSlice,
    PayloadAction,
    ThunkDispatch,
} from "@reduxjs/toolkit"

import { GlobalState } from "../../store"
import { addErrorNotification } from "../notifications/actions"
import { fetchFormattingSettings } from "../common/userFormattingSettings/slice"

import { fieldsAPI } from "../../apis"
import { attachmentsReducersBuilder } from "./components/attachments/thunks"
import { RequestErrorDataApi } from "../../errors"
import { IUserFormattingSettings } from "../applicationheader/components/usermenu"
import { IItem } from "../../models"
import { IField } from "./index"
import { ITenForceWindow } from "../../global"
import { Exceptions } from "../../util/Translations"
import { formatString } from "../../util/Strings"

declare let window: ITenForceWindow

const {
    AnErrorOccurredWhileFetchingFields = "Something went wrong when fetching fields.",
    AnErrorOccurredWhileUpdatingOtherFields = "Something went wrong when updating other fields.",
    AnErrorOccurredWhileUpdatingThisField = 'An error occurred while updating this field: ["{0}"].',
} = Exceptions

interface SetFeedbackKey {
    fieldId: string
    feedbackKey: string
}

export const fieldsSlice = createSlice({
    name: "fields",

    initialState: {
        fields: {},
        feedbackKeys: {},
        isInitialized: false,
        isLoading: false,
        id: -1,
    } as IItem,

    reducers: {
        setItemId: (state, action: PayloadAction<number>) => {
            if (state.id < 0) {
                state.id = action.payload
            }
        },
        setFeedbackKey: (state, action: PayloadAction<SetFeedbackKey>) => {
            const {
                payload: { feedbackKey, fieldId },
            } = action
            if (state?.feedbackKeys) {
                state.feedbackKeys[fieldId] = feedbackKey
            }
        },
        setFields(state, action: PayloadAction<IField[]>) {
            const fields = action.payload
            const newFields: Record<string, IField> = {}

            fields.map((field: IField) => {
                if (!!field.fieldId) {
                    newFields[field.fieldId] = field
                }
            })

            state.fields = newFields
            state.isLoading = false
            state.isInitialized = true
            state.isFetchingFields = false
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchFields.pending, (state) => {
                state.isFetchingFields = true
                state.isLoading = true
            })
            .addCase(fetchFields.fulfilled, (state, action) => {
                const fields = action.payload
                const newFields: Record<string, IField> = {}

                fields.map((field: IField) => {
                    if (!!field.fieldId) {
                        newFields[field.fieldId] = field
                    }
                })

                state.fields = newFields
                state.isLoading = false
                state.isInitialized = true
                state.isFetchingFields = false
            })

        builder
            .addCase(updateField.pending, (state) => {
                state.isLoading = true
            })
            .addCase(updateField.fulfilled, (state, action) => {
                const field = action.payload
                if (state?.fields?.[field.fieldId]) {
                    state.fields[field.fieldId] = {
                        ...state.fields[field.fieldId],
                        value: field.value,
                    }
                }
                state.isLoading = false
            })

        attachmentsReducersBuilder(builder)
    },
})

export const { setItemId, setFeedbackKey, setFields } = fieldsSlice.actions

export default fieldsSlice.reducer

export const fetchFields = createAsyncThunk(
    "fields/fetchFieldsByItemId",
    async (_, { getState, dispatch }) => {
        const state = getState() as GlobalState

        const { id } = state.fields
        let userFormattingSettings = state.userFormattingSettings

        if (!userFormattingSettings.isInitialized) {
            const response = await dispatch(fetchFormattingSettings())
            userFormattingSettings = response.payload as IUserFormattingSettings
        }

        let fields: IField[] = []

        try {
            fields = await fieldsAPI.getFields(id, userFormattingSettings)
        } catch (error) {
            console.error(error)
            dispatch(addErrorNotification(AnErrorOccurredWhileFetchingFields))
        }

        return fields
    },
    {
        condition: (_, { getState }) => {
            const { fields } = getState() as GlobalState
            if (fields.id === -1 || fields.isFetchingFields) {
                // no react fields are present or
                // fetching is in progress
                return false
            }
        },
    },
)

interface UpdateField {
    field: IField
    newValue: unknown
}

export const handleFieldUpdateFeedback = async (
    feedbackKeys: Record<string, string>,
    field: IField,
    dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
): Promise<void> => {
    if (!!feedbackKeys[field.fieldId]) {
        // we have to start an IPE feedback

        const feedbackProps = {
            key: feedbackKeys[field.fieldId],
            // we ignore because we've already refetched the react fields
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            value: function (): void {},
            error: (error: unknown): void => {
                console.error(error)
                dispatch(
                    addErrorNotification(
                        AnErrorOccurredWhileUpdatingOtherFields,
                    ),
                )
            },
        }
        window.feedback.register(feedbackProps)
    }
}

export const updateField = createAsyncThunk(
    "fields/updateField",
    async ({ field, newValue }: UpdateField, { getState, dispatch }) => {
        const state = getState() as GlobalState
        const { id, feedbackKeys } = state.fields

        // in case of an error, we'll return the old value
        let newField = field

        try {
            // otherwise we'll return the new field having the new value
            newField = await fieldsAPI.updateFieldValue(id, {
                ...field,
                value: newValue,
            })

            // The feedback system will also refetch the fields
            await handleFieldUpdateFeedback(feedbackKeys ?? {}, field, dispatch)
        } catch (error) {
            let errorMessage = formatString(
                AnErrorOccurredWhileUpdatingThisField,
                field.title,
            )
            if (error instanceof RequestErrorDataApi) {
                errorMessage = `${errorMessage} ${error.message}`
            } else if (error instanceof Error) {
                errorMessage = `${errorMessage} ${error.message}`
            }
            console.error(errorMessage, error)
            dispatch(addErrorNotification(errorMessage))
        }
        return newField
    },
)
